import { useQuery } from '@tanstack/react-query';
import { billingApi } from '@/api/endpoints/billing';
import type { SubscriptionPlanPublic } from '@/api/types';

export interface PlanPrices {
  monthly: SubscriptionPlanPublic | null;
  yearly: SubscriptionPlanPublic | null;
  /** Скидка годового относительно 12 месячных, в процентах; считается из цен (BILL-6). */
  yearlyDiscountPct: number | null;
  /** Тарифы ещё не пришли — карточки показывают плейсхолдер вместо цены. */
  isLoading?: boolean;
}

/** Бэкенд отдаёт decimal строкой («9.00») — приводим к числу один раз. */
function withNumericPrice(plan: SubscriptionPlanPublic | undefined): SubscriptionPlanPublic | null {
  return plan ? { ...plan, price: Number(plan.price) } : null;
}

export function derivePlanPrices(plans: SubscriptionPlanPublic[] | undefined): PlanPrices {
  const monthly = withNumericPrice(plans?.find((p) => p.slug === 'monthly'));
  const yearly = withNumericPrice(plans?.find((p) => p.slug === 'yearly'));
  const monthlyPrice = monthly ? Number(monthly.price) : 0;
  const yearlyPrice = yearly ? Number(yearly.price) : 0;
  const yearlyDiscountPct =
    monthly && yearly && monthlyPrice > 0
      ? Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100)
      : null;
  return { monthly, yearly, yearlyDiscountPct };
}

export function formatPrice(plan: SubscriptionPlanPublic | null): string {
  if (!plan) return '—';
  const symbol = plan.currency === 'USD' ? '$' : `${plan.currency} `;
  const price = Number(plan.price);
  return `${symbol}${Number.isInteger(price) ? price : price.toFixed(2)}`;
}

/** Тарифы из GET /subscription-plans — единственный источник цен в интерфейсе. */
export function useSubscriptionPlans() {
  const query = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => billingApi.plans(),
    select: (response) => derivePlanPrices(response.data),
    staleTime: 10 * 60_000,
  });
  return { ...(query.data ?? derivePlanPrices(undefined)), isLoading: query.isPending };
}
